import Vue from "vue";
import { captureException } from "@/utils/reporting/reportingService.js";
import systemMessageCodes from '../../config/systemMessageCodes';
import ChatMessagingService from "../../platform/chat/services/ChatMessagingService.js";
import TwilioChatService from "../../third-party/twilio/chat/services/TwilioChatService";

const initialStateChatModule = Object.freeze({
    rooms: {},
    archivedRooms: {},
    roomMessages: {},
    currentRoom: {},
    me: {},
    latestMsgArrivedDatePerRoom: {},
    lastSeenMsgDatePerRoom: {},
    lastSeenMsgDatePerRoomSession: {},
    nrOfUnreadMessages: 0,
    showUnreadMsgBadge: false,
    listenNewMsgsGlobally: true,
    attachmentPerMessage: {},
    chatReady: false,
    chatInitiated: false
});

export default {
    state: {
        roomDescriptors: [],
        rooms: {},
        archivedRooms: {},
        roomMessages: {},
        currentRoom: {},
        me: {},
        latestMsgArrivedDatePerRoom: {},
        lastSeenMsgDatePerRoom: {},
        lastSeenMsgDatePerRoomSession: {},  // this is used to update the latest seen msgs in server but only send session not all dates
        nrOfUnreadMessages: 0,
        showUnreadMsgBadge: false,
        listenNewMsgsGlobally: true,
        attachmentPerMessage: {},  //messageId:attachmentUrl,
        chatReady: false,
        chatInitiated: false,
        currentChatUserId: undefined,
        messagingService: undefined,
        chatError: '',
        currentUserMemberSidInRoom: {},
        notificationTitle: "",
        notificationMessage: ""
    },
    getters: {
        notificationTitle: state => state.notificationTitle,
        notificationMessage: state => state.notificationMessage,
        rooms: state => state.rooms,
        roomDescriptors: state => state.roomDescriptors,
        archivedRooms: state => state.archivedRooms,
        roomMessages: state => state.roomMessages,
        currentRoom: state => state.currentRoom,
        chatMe: state => state.me,
        latestMsgArrivedDatePerRoom: state => state.latestMsgArrivedDatePerRoom,
        lastSeenMsgDatePerRoom: state => state.lastSeenMsgDatePerRoom,
        lastSeenMsgDatePerRoomSession: state => state.lastSeenMsgDatePerRoomSession,
        nrOfUnreadMessages: state => state.nrOfUnreadMessages,
        showUnreadMsgBadge: state => state.showUnreadMsgBadge,
        listenNewMsgsGlobally: state => state.listenNewMsgsGlobally,
        attachmentPerMessage: state => state.attachmentPerMessage,
        chatReady: state => state.chatReady,
        chatInitiated: state => state.chatInitiated,
        currentChatUserId: state => state.currentChatUserId,
        currentUserMemberSidInRoom: state => state.currentUserMemberSidInRoom,
        messagingService: state => state.messagingService,
        chatModule: state => state,
        chatError: state => state.chatError
    },
    mutations: {
        SET_USER_NOTIFICATION(state, {title, message}){
            state.notificationTitle = title
            state.notificationMessage = message
        },
        SET_CURRENT_USER_MEMBER_IS_IN_ROOM(state, {roomId, memberSid}){
            state.currentUserMemberSidInRoom[roomId] = memberSid;
        },
        SET_ROOM_DESCRIPTORS(state, payload){
            state.roomDescriptors = payload
        },
        SET_CHAT_INITIATED(state, payload) {
            state.chatInitiated = payload
        },
        SET_CHAT_READY_STATE(state, payload) {
            state.chatReady = payload
        },
        setListenNewMsgsGlobally(state, listen) {
            state.listenNewMsgsGlobally = listen
        },
        setNrOfUnreadMessages(state, howMany) {
            state.nrOfUnreadMessages = howMany
        },
        toggleShowUnreadMsgBadge(state, show) {
            state.showUnreadMsgBadge = show
        },
        SET_CHAT_ERROR(state, errorMessage){
            state.chatError = errorMessage;
        },
        // Remove a message from room messages based on message id.
        removeFromRoomMessages(state, message){
            for (let i = 0, n = state.roomMessages[message.roomId].length; i < n; i++) {
                if(state.roomMessages[message.roomId][i].id == message.id){
                    state.roomMessages[message.roomId].splice(i, 1);
                }
            }
        },
        setLastSeenMsgDatePerRoom(state, lastSeenPerRoom) {
            state.lastSeenMsgDatePerRoom = lastSeenPerRoom
        },
        setLastSeenMsgDateForRoom(state, lastSeenDatePerRoom) {
            state.lastSeenMsgDatePerRoom[lastSeenDatePerRoom.roomId] = lastSeenDatePerRoom.lastSeenDate
        },
        setLastSeenMsgDateForRoomSession(state, lastSeenDatePerRoom) {
            state.lastSeenMsgDatePerRoomSession[lastSeenDatePerRoom.roomId] = lastSeenDatePerRoom.lastSeenDate
        },
        setLatestMsgArrivedDatePerRoom(state, latestArrivedDatePerRoom) {
            state.latestMsgArrivedDatePerRoom[latestArrivedDatePerRoom.roomId] = latestArrivedDatePerRoom.arrivedDate
        },
        setRooms(state, rooms) {
            state.rooms = rooms;
        },
        setRoomsArchived(state, roomArchived) {
            state.archivedRooms = roomArchived;
        },
        setMe(state, me) {
            state.me = me;
        },
        setCurrentChatUserId(state, id){
            state.currentChatUserId = id;
        },
        setMessagingService(state, service){
            state.messagingService = service
        },
        setCurrentRoom(state, room) {
            state.currentRoom = room;
        },
        addNewRoom(state, room) {
            if (!room.id) {
                throw Error('store | addNewRoom() > err: room.id undefined');
            }
            Vue.set(state.rooms, room.id, room)
            if (!state.roomMessages[room.id]) {
                Vue.set(state.roomMessages, room.id, []);
            }
        },
        addNewArchivedRoom(state, room) {
            if (!room.id) {
                throw Error('No room.id defined on the ChatKit Room object');
            }
            state.archivedRooms[room.id] = room;
        },
        removeRoom(state, roomId) {
            if (!roomId) {
                throw Error('STORE | Argument roomId is undefined');
            }
            delete state.rooms[roomId]
        },
        removeArchivedRoom(state, roomId) {
            if (!roomId) {
                throw Error('STORE | Argument roomId is undefined');
            }
            delete state.archivedRooms[roomId]
        },
        removeMessages(state, roomId) {
            if (!roomId) {
                throw Error('STORE | Argument roomId is undefined');
            }
            delete state.roomMessages[roomId]
        },
        ADD_MESSAGE_TO_ROOM(state, message) {

            let roomId = message.roomId
            // If messages array is missing for this room, create.
            if (!state.roomMessages[roomId]) {
                Vue.set(state.roomMessages, roomId, []);
            }

            //If message was not local temp (i.e coming from servers), remove the temp unsent message
            if(!message.unsent){
                // WARN: Removes the first unsent. If the temp message creation and server arrivals are off in order, this will cause issues.
                for (var i = state.roomMessages[roomId].length; i-- > 0;) {
                    if (state.roomMessages[roomId][i] && state.roomMessages[roomId][i].unsent){
                        state.roomMessages[roomId].splice(i, 1);
                        break;
                    } 
                }
            }

            // detect hidden messages designed to piggyback on messaging infra (e.g request real-time updates)
            if(message.attributes && message.attributes.systemCode){
                if (message.attributes.systemCode == systemMessageCodes.hiddenMessageCode || 
                    message.attributes.systemCode == systemMessageCodes.requestUpdateCode){
                    message.hidden = true
                } 
                else if (message.attributes.systemCode == systemMessageCodes.autoMessageCode) message.auto = true
            }

            // ### Notify new messagess
            if (message.isOther && !message.hidden) {
                state.latestMsgArrivedDatePerRoom[roomId] = message.createdAt
                if (state.currentRoom.id == roomId) {
                    state.lastSeenMsgDatePerRoom[roomId] = message.createdAt
                    state.lastSeenMsgDatePerRoomSession[roomId] = message.createdAt
                } else {
                    if (!state.lastSeenMsgDatePerRoom[roomId] || message.createdAt > state.lastSeenMsgDatePerRoom[roomId]) {
                        if (state.rooms[roomId]) state.rooms[roomId].unseenMsg = true
                        else if (state.archivedRooms[roomId]) state.archivedRooms[roomId].unseenMsg = true
                    }
                }
            }
            if (state.rooms[roomId]) state.rooms[roomId].lastMessage = message
            // ### Push message to room
            state.roomMessages[roomId].push(message);
        },
        initiateRoomMessagesByReset(state, allRooms) {
            for (let r of allRooms) {
                Vue.set(state.roomMessages, r.sid, []);
            }
        },
        RESET_CHAT_STATE(state) {
            state.rooms = JSON.parse(JSON.stringify(initialStateChatModule.rooms));
            state.archivedRooms = JSON.parse(JSON.stringify(initialStateChatModule.archivedRooms));
            state.roomMessages = JSON.parse(JSON.stringify(initialStateChatModule.roomMessages));
            state.currentRoom = JSON.parse(JSON.stringify(initialStateChatModule.currentRoom));
            state.me = JSON.parse(JSON.stringify(initialStateChatModule.me));
            state.latestMsgArrivedDatePerRoom = JSON.parse(JSON.stringify(initialStateChatModule.latestMsgArrivedDatePerRoom));
            state.lastSeenMsgDatePerRoom = JSON.parse(JSON.stringify(initialStateChatModule.lastSeenMsgDatePerRoom));
            state.lastSeenMsgDatePerRoomSession = JSON.parse(JSON.stringify(initialStateChatModule.lastSeenMsgDatePerRoomSession));
            state.nrOfUnreadMessages = JSON.parse(JSON.stringify(initialStateChatModule.nrOfUnreadMessages));
            state.showUnreadMsgBadge = JSON.parse(JSON.stringify(initialStateChatModule.showUnreadMsgBadge));
            state.listenNewMsgsGlobally = JSON.parse(JSON.stringify(initialStateChatModule.listenNewMsgsGlobally));
            state.attachmentPerMessage = JSON.parse(JSON.stringify(initialStateChatModule.attachmentPerMessage));
            state.chatReady = JSON.parse(JSON.stringify(initialStateChatModule.chatReady));
            state.chatInitiated = JSON.parse(JSON.stringify(initialStateChatModule.chatInitiated));
        }
    },
    actions: {
        async retrieveMessages(store, {room, filter}){
            // NOTE: This is currently only called when Chat view is visited meaning new messages are not loaded upon login
            try {
                var messages = await room.getMessages(filter)
                messages.forEach(message => {
                    store.dispatch('handleOnNewMessage', message)
                });
                return messages;
            } catch (error) {
                console.log('error', error);
                
                return []
            }
        },
        sendMessage({ state, commit, dispatch }, { message }){
            try {               
                state.messagingService.sendMessage(message)
                commit("ADD_MESSAGE_TO_ROOM", message)      // will be replaced in ADD_MESSAGE_TO_ROOM when triggered from hook with unsent: flag
            } catch (error) {
                console.log('(store)sendMessage > error:', error);
                commit("removeFromRoomMessages", message);
            }
        },
        changeCurrentRoom(context, room) {
            context.commit('setCurrentRoom', room)
        },
        archiveRoom(context, room) {},
        async initiateChat(store, uid) {
            try {
                if (store.getters.chatInitiated) return;
                store.commit("RESET_CHAT_STATE");
                // WARN: Do not move or remove this as it stops from multiple initiations (and consecutive event handlers from being set)
                store.commit("SET_CHAT_INITIATED", true);

                const hooks = {
                    onAddedToRoom: (room) => {
                        // console.log('STORE | onAddedToRoom | room', room);
                        room?store.commit('addNewRoom', room):void 0;
                    },
                    onMessage: (message) => {
                        store.dispatch('handleOnNewMessage', message)
                    },
                    connectionError: (error) => {
                        console.log('STORE | connectionError | error', error);
                        throw error
                    }
                };

                var messagingService = new ChatMessagingService(new TwilioChatService())
                await messagingService.init(hooks)

                var roomDescriptors = await messagingService.getAllRoomDescriptors()

                store.commit("SET_ROOM_DESCRIPTORS", roomDescriptors)
                store.commit('setCurrentChatUserId', messagingService.getUser().identity)
                store.commit('setMessagingService', messagingService)
                store.commit('initiateRoomMessagesByReset', roomDescriptors);
                store.commit('SET_CHAT_READY_STATE', true);
                
            }
            catch (error) {
                console.log("initiateChat > error: ", error)
                store.commit("SET_CHAT_ERROR", error)
                store.commit("SET_CHAT_INITIATED", false);
                captureException('store.initiateChat()', error);
            }
        },
        handleOnNewMessage(store, message){
            if (message.authorIdentity != store.getters.currentChatUserId) message.setIsOther(true);
            store.commit('ADD_MESSAGE_TO_ROOM', message);
            
            if (
                message.isOther &&
                store.getters.listenNewMsgsGlobally &&
                message.roomLastConsumedMessageIndex < message.index
                ) {
                    store.commit('toggleShowUnreadMsgBadge', true);
                    store.commit('SET_USER_NOTIFICATION', {title:"Chat message!", message: message.textMessage})
            }

        }
    }
}



// WEBPACK FOOTER //
// ./src/vuex/chat/index.js